import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, Building2, CreditCard, Landmark, Radar, Cpu, TrendingUp, FileText, Gauge, Network,
} from "lucide-react";
import { PublicNav, PublicFooter, PublicHero } from "@/components/public/PublicChrome";
import { useAuth } from "@/context/AuthContext";

const PRODUCTS = [
  { icon: ShieldCheck, name: "Personal Credit Intelligence", tag: "Foundation", accent: "#1a5fd0",
    body: "Track your FICO across bureaus, see the exact factors moving your score, and follow a prioritized plan to raise it.",
    to: "/personal-credit", points: ["Multi-bureau score tracking", "Score simulator", "Personalized action plan"] },
  { icon: Building2, name: "Business Credit Intelligence", tag: "Scalability", accent: "#b0862f",
    body: "Build a fundable business file across D&B, Experian and Equifax Commercial — PAYDEX, tradelines and utilization in one view.",
    to: "/business-credit", points: ["PAYDEX & Intelliscore tracking", "Tradeline monitoring", "Fundability signals"] },
  { icon: CreditCard, name: "Credit Builder", tag: "Build", accent: "#059669",
    body: "Report rent, utilities and bills, open reporting tradelines, and add a builder account — every credit-building tool, native to one platform.",
    to: "/credit-builder", points: ["Rent & bill reporting", "Reporting tradelines", "Credit builder account"] },
  { icon: Landmark, name: "Funding Marketplace", tag: "Capital", accent: "#1a5fd0",
    body: "Explore cards, loans, lines and vendors — with the bureaus each reports to spelled out — then get matched to what you qualify for.",
    to: "/marketplace", points: ["Curated provider directory", "Loan-type explainers", "Lender matching"] },
  { icon: Gauge, name: "Funding Readiness Engine", tag: "Readiness", accent: "#b0862f",
    body: "A single readiness score that tells you exactly what to fix before you apply — so you apply where you'll actually get a 'yes'.",
    to: "/funding-readiness", points: ["Readiness scoring", "Gap checklist", "Apply-when-ready guidance"] },
  { icon: Radar, name: "Millionaire Adaptive OS", tag: "Gen-UI", accent: "#1a5fd0",
    body: "An intent-driven interface that assembles the right tools, data and next steps around whatever you're trying to accomplish.",
    to: "/adaptive", points: ["Ask in plain language", "Generative workspaces", "Guided pathways"] },
  { icon: Network, name: "MMT Financial Graph", tag: "Data", accent: "#059669",
    body: "Every entity, account, tradeline and signal — connected into one live picture of your personal and business finances.",
    to: "/financial-graph", points: ["Connected entities", "Unified accounts", "Live signals"] },
  { icon: Cpu, name: "The Capital Crew", tag: "AI Workforce", accent: "#b0862f",
    body: "A team of AI specialists that draft documents, prep applications, monitor your file and move your plan forward while you run your business.",
    to: "/adaptive", points: ["Document drafting", "Application prep", "Always-on monitoring"] },
];

export default function Products() {
  const { user } = useAuth();
  const primaryTo = user ? "/adaptive" : "/register";
  return (
    <div className="min-h-screen bg-[#f7f4ec]" data-testid="products-page">
      <PublicNav />
      <PublicHero
        eyebrow="Products"
        title="Every credit & funding tool,"
        accent="under one roof."
        sub="You used to stitch together five subscriptions to build credit and get funded. MMT puts personal credit, business credit, credit building, funding and an AI workforce in a single platform."
      />
      <div className="mx-auto max-w-7xl px-5 pb-16 pt-10 lg:px-8">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {PRODUCTS.map((p) => (
            <Link
              key={p.name}
              to={user ? p.to : "/register"}
              data-testid={`product-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              className="group flex flex-col rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${p.accent}15` }}>
                <p.icon size={20} style={{ color: p.accent }} />
              </span>
              <div className="mt-4 flex items-center gap-2">
                <span className="rounded-full border border-slate-200 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-[#a37d2a]">{p.tag}</span>
              </div>
              <h3 className="mt-2 font-head text-[17px] font-bold text-slate-900">{p.name}</h3>
              <p className="mt-2 flex-1 text-[13px] leading-relaxed text-slate-500">{p.body}</p>
              <ul className="mt-3 space-y-1">
                {p.points.map((pt) => (
                  <li key={pt} className="flex items-center gap-2 text-[12px] text-slate-600"><span className="h-1 w-1 rounded-full" style={{ background: p.accent }} />{pt}</li>
                ))}
              </ul>
              <span className="mt-4 flex items-center gap-1.5 text-[13px] font-semibold text-[#1a5fd0] transition-all group-hover:gap-2.5">Explore <ArrowRight size={14} /></span>
            </Link>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-start gap-4 rounded-2xl bg-[#0f172a] p-8 sm:flex-row sm:items-center sm:justify-between" data-testid="products-cta">
          <div>
            <div className="font-serif-display text-2xl font-medium text-white">Start with the free plan.</div>
            <p className="mt-1 text-[14px] text-slate-300">See where you stand today — no card required — and unlock building tools when you're ready.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/pricing" className="rounded-xl border border-white/20 px-5 py-3 text-[13px] font-semibold text-white hover:bg-white/10">See pricing</Link>
            <Link to={primaryTo} data-testid="products-cta-start" className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#b0862f] to-[#d8b25a] px-6 py-3 text-[13px] font-semibold text-[#0f172a] active:scale-95">Get Started Free <ArrowRight size={15} /></Link>
          </div>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
